import type {
	NewSearchSuggestProps,
	Suggestion
} from '~/components/SearchSuggest';

import { SEARCH_SUGGEST } from '~/constants';
import { nextTick, watch } from 'vue';

export interface UseSearchSuggestDialogSharedHandlersOptions {
	isOpen: Ref<boolean>;
	props: NewSearchSuggestProps;
	searchTerm: Ref<string>;
	isUpdatingFromWatch: Ref<boolean>;
	clear: (reset?: boolean) => void;
	executeLocationAutocomplete: () => void;
	handleApplySearchTerm: () => void;
	baseHandleModelValueUpdate: (
		suggestion: Suggestion,
		navigate: boolean
	) => void;
	/** After confirming a suggestion in the modal (close + host-specific side effects). */
	afterSelectSuggestion: () => void;
	/** Close the modal (back button, host-defined overlay handling, code fetch finished). */
	closeModal: () => void;
	/** Trigger clear icon: SRP uses clear(); Shorts uses clear(false). */
	triggerClearUsesFullClear: boolean;
}

/**
 * Shared dialog behavior for SuggestDialog (SRP) and ShortsSearchSuggestDialog.
 * Hosts customize via afterSelectSuggestion, closeModal, and triggerClearUsesFullClear.
 */
export function useSearchSuggestDialogSharedHandlers(
	options: UseSearchSuggestDialogSharedHandlersOptions
) {
	const {
		isOpen,
		props,
		searchTerm,
		isUpdatingFromWatch,
		clear,
		executeLocationAutocomplete,
		handleApplySearchTerm,
		baseHandleModelValueUpdate,
		afterSelectSuggestion,
		closeModal,
		triggerClearUsesFullClear
	} = options;

	function openDialog() {
		const run = () => {
			isOpen.value = true;
			clear(false);

			if (searchTerm.value.length >= SEARCH_SUGGEST.MIN_SEARCH_LENGTH) {
				executeLocationAutocomplete();
			}
		};
		if (import.meta.client) {
			requestAnimationFrame(run);
		} else {
			run();
		}
	}

	function handleModelValueUpdate(suggestion: Suggestion) {
		baseHandleModelValueUpdate(suggestion, true);
		nextTick(() => {
			searchTerm.value = '';
		});
		afterSelectSuggestion();
	}

	async function handleInputClear() {
		if (triggerClearUsesFullClear) {
			clear();
		} else {
			clear(false);
		}
		isOpen.value = true;

		if (searchTerm.value.length >= SEARCH_SUGGEST.MIN_SEARCH_LENGTH) {
			executeLocationAutocomplete();
		}

		await nextTick();
	}

	watch(
		() => props.searchTerm,
		value => {
			if (value && value.trim()) {
				handleApplySearchTerm();
			} else if (value === undefined || value === null) {
				isUpdatingFromWatch.value = true;
				searchTerm.value = '';
			}
		},
		{ immediate: true }
	);

	watch(
		() => props.isFetchingPropertyByCodeSearch,
		isFetching => {
			if (!isFetching) closeModal();
		}
	);

	return {
		openDialog,
		handleModelValueUpdate,
		handleInputClear
	};
}
